// Procedural fallback primitives. Playable before any art lands, and the
// mandatory fallback if a sprite sheet fails to load (AGENT.md §3).
// Swapped for real atlas frames in Phase 4.

import { Container, Graphics, Sprite, Texture } from 'pixi.js';
import type { Breed, ChickenState, TerrainType } from '../core/types';
import { TILE_HEIGHT, TILE_WIDTH } from './iso';
import { BREED_CLAY, CLAY, shade, TERRAIN_CLAY } from './palette';
import { birdShadow, shadowForDrawnWidth } from './depth';
import { shadowScale, shadowTexture } from './shadowTexture';
import type { SpriteMeta } from './spriteMeta';

/** Flat diamond tile with a short extruded side, drawn at local origin. */
export function drawTile(g: Graphics, terrain: TerrainType, dirtiness = 0): Graphics {
  const hw = TILE_WIDTH / 2;
  const hh = TILE_HEIGHT / 2;
  const skirt = 10;
  // Soiled litter reads as a darker, muddier top face.
  const top = shade(TERRAIN_CLAY[terrain], (dirtiness / 100) * 0.35);

  g.clear();
  g.beginFill(shade(top, 0.35));
  g.drawPolygon([-hw, hh, 0, TILE_HEIGHT, hw, hh, hw, hh + skirt, 0, TILE_HEIGHT + skirt, -hw, hh + skirt]);
  g.endFill();

  g.lineStyle({ width: 1, color: shade(top, 0.45), alpha: 0.5 });
  g.beginFill(top);
  g.drawPolygon([0, 0, hw, hh, 0, TILE_HEIGHT, -hw, hh]);
  g.endFill();
  g.lineStyle(0);
  return g;
}

/** Translucent overlay marking a hovered or selected tile. */
export function drawTileHighlight(g: Graphics, color: number, alpha = 0.35): Graphics {
  const hw = TILE_WIDTH / 2;
  const hh = TILE_HEIGHT / 2;
  g.clear();
  g.lineStyle({ width: 3, color, alpha: 0.9 });
  g.beginFill(color, alpha);
  g.drawPolygon([0, 0, hw, hh, 0, TILE_HEIGHT, -hw, hh]);
  g.endFill();
  g.lineStyle(0);
  return g;
}

/**
 * Contact-shadow opacity. Low on purpose: the shadow's job is to say "this
 * touches the ground", and a stronger one competes with the bird for attention,
 * which is the opposite of the grounding it was added for.
 */
const BIRD_SHADOW_ALPHA = 0.3;

/**
 * Placeholder bird: stacked clay ovals sized by life stage. Anchored at the
 * bird's feet so it drops straight onto a tile centre.
 */
export interface ChickenPlaceholderOptions {
  breed: Breed;
  /** Scales the whole bird; chick ≈ 0.6, adult 1. */
  scale?: number;
  /** 12fps frame index — drives the stop-motion bob. */
  frame?: number;
  state?: ChickenState;
}

export class ChickenPlaceholder extends Container {
  private body = new Graphics();
  /**
   * Contact shadow, drawn first so it sits under the body. Sized from the
   * measured pivot rather than the image box, so re-exporting a sprite at
   * another size cannot slide the shadow off the bird's feet.
   */
  private shadow = new Sprite(shadowTexture());
  private hoverRing = new Graphics();
  private sprite = new Sprite();
  private cosmetic = new Sprite();
  private breed: Breed;
  private baseScale: number;
  /** Target on-screen height, so any source resolution lands the same size. */
  private targetHeight: number;

  constructor(options: ChickenPlaceholderOptions) {
    super();
    this.breed = options.breed;
    this.baseScale = options.scale ?? 1;
    this.targetHeight = 92 * this.baseScale;
    this.sprite.anchor.set(0.5, 1);
    this.sprite.roundPixels = true;
    this.sprite.visible = false;
    this.hoverRing.visible = false;
    // R5 wardrobe: same-canvas overlay, blits at (0,0) with the body's
    // transform (DROP-ASSETS.md). Drawn last = worn on top.
    this.cosmetic.anchor.set(0.5, 1);
    this.cosmetic.roundPixels = true;
    this.cosmetic.visible = false;
    // Centred on the container origin, which is where the sprite's measured
    // pivot puts the bird's feet — the one point every breed agrees on.
    this.shadow.anchor.set(0.5);
    this.shadow.tint = CLAY.shadow;
    this.shadow.alpha = BIRD_SHADOW_ALPHA;
    this.addChild(this.shadow, this.hoverRing, this.body, this.sprite, this.cosmetic);
    this.redraw();
    this.setFrame(options.frame ?? 0, options.state ?? 'IDLE');
  }

  /**
   * Swaps in real art when it exists. Passing null keeps the primitive, which
   * is the normal state until the sprite phases are generated.
   *
   * `meta` comes from the offline pipeline and carries the feet position and
   * the standing height. Without it the only available reference is the image
   * box, which differs per render — that is why breeds used to come out at
   * inconsistent sizes and hovering above their tile.
   */
  setTexture(texture: Texture | null, meta: SpriteMeta | null = null): void {
    const usingArt = texture !== null;
    if (usingArt && this.sprite.texture !== texture) {
      this.sprite.texture = texture;
      if (meta) {
        this.sprite.anchor.set(meta.pivotX / meta.w, meta.pivotY / meta.h);
        this.sprite.scale.set(this.targetHeight / (meta.standHeight ?? meta.h));
      } else {
        this.sprite.anchor.set(0.5, 1);
        this.sprite.scale.set(this.targetHeight / texture.height);
      }
      // The measurement is the preferred source for both the anchor and the
      // scale, and the shadow is re-derived from the same place so it cannot
      // drift. An unmeasured sprite is scaled from its own box, and the shadow
      // then follows that drawn width — whatever is on screen is what is
      // shadowed, so a stale manifest cannot leave a bird on a wrong footprint.
      this.resizeShadow(
        meta
          ? birdShadow(meta, this.targetHeight)
          : shadowForDrawnWidth(texture.width * this.sprite.scale.x),
      );
    }
    this.sprite.visible = usingArt;
    // The primitive body hides rather than unmounts, so the swap is reversible.
    this.body.visible = !usingArt;
    this.syncCosmetic();
  }

  /**
   * Fits the shared shadow patch to a measured footprint. Re-runs only when the
   * texture or breed changes, not per frame.
   */
  private resizeShadow({ rx, ry }: { rx: number; ry: number }): void {
    const fit = shadowScale(rx, ry);
    this.shadow.scale.set(fit.x, fit.y);
  }

  /**
   * R5 wardrobe: equips a cosmetic overlay, or clears it. The trim copies the
   * body's anchor/scale so same-canvas art lands pixel-true; on the primitive
   * it fits to target height the same way.
   */
  setCosmetic(texture: Texture | null): void {
    if (texture && this.cosmetic.texture !== texture) {
      this.cosmetic.texture = texture;
    }
    this.cosmetic.visible = texture !== null;
    this.syncCosmetic();
  }

  private syncCosmetic(): void {
    if (!this.cosmetic.visible) return;
    const src = this.sprite.visible ? this.sprite : null;
    if (src) {
      this.cosmetic.anchor.copyFrom(src.anchor);
      this.cosmetic.scale.copyFrom(src.scale);
    } else {
      this.cosmetic.anchor.set(0.5, 1);
      this.cosmetic.scale.set(this.targetHeight / this.cosmetic.texture.height);
    }
  }

  hasArt(): boolean {
    return this.sprite.visible;
  }

  /**
   * Ring at the bird's feet while the pointer is over it. It sits on the ground
   * rather than outlining the body so it reads the same whether the bird is the
   * primitive or real art, and it never fights the facing flip.
   */
  setHovered(hovered: boolean): void {
    this.hoverRing.visible = hovered;
  }

  /** Left-facing views are engine-mirrored — the art only ships right views. */
  setFacing(facing: 'left' | 'right'): void {
    const flip = facing === 'left' ? -1 : 1;
    this.sprite.scale.x = Math.abs(this.sprite.scale.x) * flip;
    this.cosmetic.scale.x = Math.abs(this.cosmetic.scale.x) * flip;
    this.body.scale.x = flip;
  }

  private redraw(): void {
    const { body, comb } = BREED_CLAY[this.breed];
    const s = this.baseScale;
    const h = 44 * s;
    const w = 30 * s;

    // The primitive has no measurement, so its shadow comes from the same
    // proportions as the body it stands under. Redrawing is also how a breed
    // change lands, and that must not overwrite a shadow already sized from the
    // measured art the bird is actually showing.
    if (!this.sprite.visible) this.resizeShadow({ rx: w * 0.7, ry: w * 0.35 });

    this.hoverRing.clear();
    this.hoverRing.lineStyle({ width: 3 * s, color: CLAY.cream, alpha: 0.9 });
    this.hoverRing.drawEllipse(0, 0, w * 0.85, w * 0.45);

    const g = this.body;
    g.clear();
    // Body
    g.lineStyle({ width: 2 * s, color: CLAY.outline, alpha: 0.55 });
    g.beginFill(body);
    g.drawEllipse(0, -h * 0.45, w * 0.55, h * 0.4);
    g.endFill();
    // Head
    g.beginFill(body);
    g.drawCircle(w * 0.15, -h * 0.85, w * 0.3);
    g.endFill();
    // Comb
    g.beginFill(comb);
    g.drawCircle(w * 0.15, -h * 1.08, w * 0.13);
    g.endFill();
    // Beak
    g.beginFill(CLAY.accent);
    g.drawPolygon([w * 0.42, -h * 0.85, w * 0.68, -h * 0.8, w * 0.42, -h * 0.75]);
    g.endFill();
    // Legs
    g.lineStyle({ width: 3 * s, color: CLAY.accent });
    g.moveTo(-w * 0.15, -h * 0.1);
    g.lineTo(-w * 0.15, 0);
    g.moveTo(w * 0.15, -h * 0.1);
    g.lineTo(w * 0.15, 0);
    g.lineStyle(0);
  }

  /**
   * Applies the 12fps pose. Deliberately snappy — no interpolation. With real
   * art this drives frame selection; with the primitive it drives the bob.
   */
  setFrame(frame: number, state: ChickenState): void {
    if (this.sprite.visible) return;
    const phase = frame % 4;
    switch (state) {
      case 'WALKING':
        this.body.y = phase === 1 || phase === 3 ? -2 : 0;
        this.body.rotation = (phase - 1.5) * 0.02;
        break;
      case 'PECKING':
        this.body.y = phase < 2 ? 0 : 4;
        this.body.rotation = phase < 2 ? 0 : 0.22;
        break;
      case 'ROOSTING':
      case 'BROODY':
        this.body.y = 3;
        this.body.rotation = 0;
        break;
      default:
        this.body.y = phase === 0 ? -1 : 0;
        this.body.rotation = 0;
    }
  }

  setBreed(breed: Breed): void {
    if (breed === this.breed) return;
    this.breed = breed;
    this.redraw();
  }
}

